import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ApiKeyModal from './ApiKeyModal';
import { generateVideo, checkVideoStatus, remixVideo } from '../services/apimartApi';

const {
  FiPlay,
  FiSettings,
  FiZap,
  FiVideo,
  FiClock,
  FiMonitor,
  FiSmartphone,
  FiDownload,
  FiTrash2,
  FiExternalLink,
  FiAlertCircle,
  FiRefreshCw,
  FiInfo,
  FiCode,
  FiCheckCircle,
  FiShoppingBag,
  FiHeart,
  FiMail,
  FiFileText,
  FiShield,
  FiRocket,
  FiCpu,
  FiFilm,
  FiArchive,
  FiImage,
  FiX
} = FiIcons;

const MAX_STORED_VIDEOS = 10;
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

const VideoGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [duration, setDuration] = useState(5);
  const [aspectRatio, setAspectRatio] = useState('portrait');
  const [model, setModel] = useState('sora2');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [showApiModal, setShowApiModal] = useState(false);
  const [hasApiKey, setHasApiKey] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [expandedDebug, setExpandedDebug] = useState(null);
  const [remixVideoId, setRemixVideoId] = useState('');
  const [showRemixInput, setShowRemixInput] = useState(false);

  // MiniMax specific state
  const [resolution, setResolution] = useState('768p');

  // Image Upload state
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const getGenerationMode = () => {
    if (remixVideoId && model === 'sora2') return 'video-to-video';
    if (uploadedImage) return 'image-to-video';
    return 'text-to-video';
  };

  const generationMode = getGenerationMode();

  useEffect(() => {
    const apiKey = localStorage.getItem('apimart_api_key');
    setHasApiKey(!!apiKey);

    const savedVideos = localStorage.getItem('generated_videos');
    if (savedVideos) {
      try {
        const videos = JSON.parse(savedVideos);
        setGeneratedVideos(videos);

        // Resume polling for unfinished videos
        videos.forEach(video => {
          if ((video.status === 'processing' || video.status === 'pending' || video.status === 'submitted') && video.taskId) {
            console.log('Resuming polling for task:', video.taskId);
            pollVideoStatus(video.taskId, video);
          }
        });
      } catch (e) {
        console.error('Error loading saved videos:', e);
      }
    }
  }, []);

  const handleSaveApiKey = (apiKey) => {
    localStorage.setItem('apimart_api_key', apiKey);
    setHasApiKey(true);
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrorMessage('Please upload a valid image file (JPEG, PNG, WebP)');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      setErrorMessage('Image size must be less than 10MB');
      return;
    }

    setUploadedImage(file);

    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);

    setErrorMessage('');
  };

  const removeImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
  };

  const pollVideoStatus = async (taskId, tempVideo) => {
    const maxAttempts = 180;
    let attempts = 0;
    let consecutiveErrors = 0;
    const maxConsecutiveErrors = 5;

    const poll = async () => {
      try {
        attempts++;
        const data = await checkVideoStatus(taskId);
        consecutiveErrors = 0;

        const status = data.status;
        const progress = data.progress || 0;

        setGeneratedVideos(prevVideos => {
          const updatedVideos = prevVideos.map(v =>
            v.id === tempVideo.id
              ? { ...v, progress: progress, status: status, debugInfo: data._raw }
              : v
          );
          localStorage.setItem('generated_videos', JSON.stringify(updatedVideos));
          return updatedVideos;
        });

        if (status === 'completed' || status === 'succeeded' || status === 'success') {
          let videoUrl = null;

          if (data.result && data.result.videos && data.result.videos.length > 0) {
            videoUrl = data.result.videos[0].url;
          } else if (data.output && Array.isArray(data.output) && data.output.length > 0) {
            videoUrl = data.output[0];
          } else if (data.output && typeof data.output === 'string') {
            videoUrl = data.output;
          }

          if (videoUrl) {
            const completedVideo = {
              ...tempVideo,
              url: videoUrl,
              status: 'completed',
              progress: 100,
              thumbnail: videoUrl,
              completedAt: new Date().toISOString(),
              debugInfo: data._raw
            };

            setGeneratedVideos(prevVideos => {
              const updatedVideos = prevVideos.map(v =>
                v.id === tempVideo.id ? completedVideo : v
              );
              localStorage.setItem('generated_videos', JSON.stringify(updatedVideos));
              return updatedVideos;
            });
            return;
          }
        }

        if (status === 'failed' || status === 'cancelled') {
          const failedVideo = {
            ...tempVideo,
            status: 'failed',
            progress: 0,
            error: data.error?.message || JSON.stringify(data.error) || 'Video generation failed',
            debugInfo: data._raw
          };

          setGeneratedVideos(prevVideos => {
            const updatedVideos = prevVideos.map(v =>
              v.id === tempVideo.id ? failedVideo : v
            );
            localStorage.setItem('generated_videos', JSON.stringify(updatedVideos));
            return updatedVideos;
          });
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        } else {
          setGeneratedVideos(prevVideos => {
            const updatedVideos = prevVideos.map(v =>
              v.id === tempVideo.id
                ? { ...v, status: 'timeout', error: 'Polling timed out' }
                : v
            );
            localStorage.setItem('generated_videos', JSON.stringify(updatedVideos));
            return updatedVideos;
          });
        }
      } catch (error) {
        console.error('❌ Error polling video status:', error);
        consecutiveErrors++;

        setGeneratedVideos(prevVideos => {
          return prevVideos.map(v =>
            v.id === tempVideo.id
              ? { ...v, lastError: error.message }
              : v
          );
        });

        if (consecutiveErrors >= maxConsecutiveErrors) {
          const errorVideo = {
            ...tempVideo,
            status: 'error',
            progress: 0,
            error: error.message || 'Network error or persistent API failure',
            debugInfo: { error: error.toString() }
          };

          setGeneratedVideos(prevVideos => {
            const updatedVideos = prevVideos.map(v =>
              v.id === tempVideo.id ? errorVideo : v
            );
            localStorage.setItem('generated_videos', JSON.stringify(updatedVideos));
            return updatedVideos;
          });
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(poll, 5000);
        }
      }
    };

    poll();
  };

  const handleGenerate = async () => {
    if (!prompt.trim() && !uploadedImage && !remixVideoId) {
      setErrorMessage('Please provide a prompt or upload an image');
      return;
    }

    if (!hasApiKey) {
      setShowApiModal(true);
      return;
    }

    setIsGenerating(true);
    setErrorMessage('');

    try {
      console.log('🎬 Starting video generation...');
      console.log('Mode:', generationMode);
      console.log('Model:', model);
      console.log('Remix Video ID:', remixVideoId);

      let response;

      if (generationMode === 'video-to-video' && remixVideoId) {
        console.log('🔄 VIDEO-TO-VIDEO REMIX MODE');
        response = await remixVideo({
          videoId: remixVideoId,
          prompt,
          duration,
          aspectRatio,
          model
        });
      } else {
        const metadata = model === 'minimax' ? { resolution } : {};

        response = await generateVideo({
          prompt,
          duration,
          aspectRatio,
          model,
          metadata,
          image: uploadedImage // Pass the file object
        });
      }

      if (response && response.data && Array.isArray(response.data) && response.data.length > 0) {
        const taskId = response.data[0].task_id;
        const status = response.data[0].status;

        const newVideo = {
          id: Date.now(),
          prompt: prompt || (remixVideoId ? 'Remix Video' : 'Image Animation'),
          duration,
          aspectRatio,
          model,
          taskId,
          status: status || 'processing',
          progress: 0,
          url: null,
          thumbnail: null,
          generationMode: generationMode,
          remixVideoId: generationMode === 'video-to-video' ? remixVideoId : undefined,
          metadata: model === 'minimax' ? { resolution } : undefined,
          hasImage: !!uploadedImage,
          createdAt: new Date().toISOString()
        };

        setGeneratedVideos(prevVideos => {
          const updatedVideos = [newVideo, ...prevVideos].slice(0, MAX_STORED_VIDEOS);
          localStorage.setItem('generated_videos', JSON.stringify(updatedVideos));
          return updatedVideos;
        });

        pollVideoStatus(taskId, newVideo);

        // Reset inputs after successful submission
        setRemixVideoId('');
        setShowRemixInput(false);
        setPrompt('');
        removeImage();
      } else {
        throw new Error('Invalid response format from API');
      }
    } catch (error) {
      console.error('❌ Error generating video:', error);
      setErrorMessage(error.message || 'Failed to generate video. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRetry = (video) => {
    if (video.taskId) {
      setGeneratedVideos(prevVideos => {
        const updatedVideos = prevVideos.map(v =>
          v.id === video.id
            ? { ...v, status: 'processing', progress: 0, error: null }
            : v
        );
        localStorage.setItem('generated_videos', JSON.stringify(updatedVideos));
        return updatedVideos;
      });

      pollVideoStatus(video.taskId, video);
    }
  };

  const handleDownload = async (video) => {
    if (!video.url) return;

    try {
      const response = await fetch(video.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `vidroid-${video.id}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to download. Try right-clicking the video and "Save As".');
    }
  };

  const handleDelete = (videoId) => {
    setGeneratedVideos(prevVideos => {
      const updatedVideos = prevVideos.filter(v => v.id !== videoId);
      localStorage.setItem('generated_videos', JSON.stringify(updatedVideos));
      return updatedVideos;
    });
  };

  const handleClearAll = () => {
    if (window.confirm('Are you sure you want to clear all saved videos? This action cannot be undone.')) {
      setGeneratedVideos([]);
      localStorage.removeItem('generated_videos');
    }
  };

  const toggleDebug = (videoId) => {
    setExpandedDebug(expandedDebug === videoId ? null : videoId);
  };

  const handleUseForRemix = (video) => {
    if (video.taskId && video.status === 'completed') {
      setRemixVideoId(video.taskId);
      setShowRemixInput(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getModelDurations = () => {
    if (model === 'sora2') {
      return [10, 15];
    } else if (model === 'veo31') {
      return [8];
    } else if (model === 'minimax') {
      if (resolution === '1080p') {
        return [5];
      }
      return [5, 10];
    }
    return [10];
  };

  useEffect(() => {
    const availableDurations = getModelDurations();
    if (!availableDurations.includes(duration)) {
      setDuration(availableDurations[0]);
    }
  }, [model, resolution]);

  useEffect(() => {
    if (model !== 'sora2') {
      setRemixVideoId('');
      setShowRemixInput(false);
    }
  }, [model]);

  return (
    <div className="min-h-screen flex flex-col pt-20">
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="flex items-center justify-center mb-6">
              <motion.div
                animate={{
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                <SafeIcon icon={FiZap} className="text-6xl text-cyan-400 drop-shadow-[0_0_20px_rgba(0,255,255,0.7)]" />
                <div className="absolute inset-0 text-6xl text-cyan-400 animate-pulse opacity-50">
                  <SafeIcon icon={FiZap} />
                </div>
              </motion.div>
            </div>

            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="text-6xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-4"
            >
              Vidroid
            </motion.h1>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.8 }}
              className="text-xl text-gray-300 font-light"
            >
              AI-Powered Video Generation with Sora 2, Veo 3.1 & MiniMax
            </motion.p>

            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.7, duration: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowApiModal(true)}
              className="mt-6 px-6 py-3 bg-gray-700/50 border border-cyan-400/30 rounded-xl text-cyan-400 hover:bg-gray-700 transition-all duration-300 flex items-center gap-2 mx-auto"
            >
              <SafeIcon icon={FiSettings} className="text-lg" />
              {hasApiKey ? 'Update Apimart API Key' : 'Configure Apimart API Key'}
            </motion.button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="bg-gray-800/50 backdrop-blur-xl rounded-3xl p-8 border border-cyan-400/20 shadow-[0_0_50px_rgba(0,255,255,0.1)]"
          >
            {/* REMIX MODE INDICATOR */}
            {showRemixInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mb-6 bg-purple-500/10 border border-purple-400/30 rounded-xl p-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiRefreshCw} className="text-purple-400 text-xl" />
                  <div>
                    <h3 className="text-purple-400 font-semibold">Remixing Video</h3>
                    <p className="text-gray-400 text-sm">Task ID: {remixVideoId}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setRemixVideoId('');
                    setShowRemixInput(false);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <SafeIcon icon={FiX} />
                </button>
              </motion.div>
            )}

            <div className="mb-8">
              <label className="block text-cyan-400 text-sm font-medium mb-4 flex items-center gap-2">
                <SafeIcon icon={FiSettings} className="text-lg" />
                AI Model
              </label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {[
                  { id: 'sora2', name: 'Sora 2', description: 'Best for Remix & Quality' },
                  { id: 'veo31', name: 'Veo 3.1 Fast', description: 'High Speed Generation' },
                  { id: 'minimax', name: 'MiniMax', description: 'Advanced Control' }
                ].map((modelOption) => (
                  <motion.button
                    key={modelOption.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setModel(modelOption.id)}
                    className={`p-3 rounded-xl border-2 transition-all duration-300 ${
                      model === modelOption.id
                        ? 'border-cyan-400 bg-cyan-400/10 shadow-[0_0_20px_rgba(0,255,255,0.3)]'
                        : 'border-gray-600 bg-gray-700/50 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-left">
                      <div className="text-white font-medium text-sm">{modelOption.name}</div>
                      <div className="text-gray-400 text-xs">{modelOption.description}</div>
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>

            {/* IMAGE UPLOAD SECTION */}
            {!showRemixInput && (
              <div className="mb-8">
                <label className="block text-cyan-400 text-sm font-medium mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiImage} className="text-lg" />
                  Reference Image (Optional)
                </label>

                {!imagePreview ? (
                  <div className="border-2 border-dashed border-gray-600 rounded-xl p-6 text-center hover:border-cyan-400 transition-colors duration-300">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="home-image-upload"
                    />
                    <label
                      htmlFor="home-image-upload"
                      className="cursor-pointer flex flex-col items-center gap-3"
                    >
                      <SafeIcon icon={FiImage} className="text-4xl text-gray-400" />
                      <div>
                        <p className="text-white font-medium">Click to upload image</p>
                        <p className="text-gray-400 text-sm mt-1">JPEG, PNG, WebP (max 10MB)</p>
                      </div>
                    </label>
                  </div>
                ) : (
                  <div className="relative">
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="w-full h-48 object-cover rounded-xl"
                    />
                    <button
                      onClick={removeImage}
                      className="absolute top-2 right-2 p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                    >
                      <SafeIcon icon={FiX} />
                    </button>
                    <div className="absolute bottom-2 left-2 px-3 py-1 bg-black/60 backdrop-blur-md rounded-lg text-xs text-white">
                      Image-to-Video Mode
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="mb-8">
              <label className="block text-cyan-400 text-sm font-medium mb-4 flex items-center gap-2">
                <SafeIcon icon={FiVideo} className="text-lg" />
                {showRemixInput ? 'Remix Prompt' : 'Video Prompt'}
              </label>

              <motion.div whileFocus={{ scale: 1.01 }} className="relative">
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={
                    showRemixInput
                      ? "Describe how you want to change the video..."
                      : "Describe the video you want to generate..."
                  }
                  className="w-full h-32 bg-gray-700/50 border border-gray-600 rounded-xl px-4 py-3 text-white placeholder-gray-400 focus:border-cyan-400 focus:outline-none focus:ring-2 focus:ring-cyan-400/20 transition-all duration-300 resize-none"
                />
                <div className="absolute bottom-3 right-3 text-gray-400 text-sm">
                  {prompt.length}/500
                </div>
              </motion.div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <label className="block text-cyan-400 text-sm font-medium mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiClock} className="text-lg" />
                  Duration
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {getModelDurations().map((dur) => (
                    <motion.button
                      key={dur}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setDuration(dur)}
                      className={`py-3 px-4 rounded-lg border-2 transition-all duration-300 ${
                        duration === dur
                          ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400 shadow-[0_0_15px_rgba(0,255,255,0.3)]'
                          : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      {dur}s
                    </motion.button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-cyan-400 text-sm font-medium mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiMonitor} className="text-lg" />
                  Aspect Ratio
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { id: 'landscape', name: 'Landscape', icon: FiMonitor },
                    { id: 'portrait', name: 'Portrait', icon: FiSmartphone }
                  ].map((ratio) => (
                    <motion.button
                      key={ratio.id}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setAspectRatio(ratio.id)}
                      className={`py-3 px-4 rounded-lg border-2 transition-all duration-300 flex items-center justify-center gap-2 ${
                        aspectRatio === ratio.id
                          ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400 shadow-[0_0_15px_rgba(0,255,255,0.3)]'
                          : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      <SafeIcon icon={ratio.icon} className="text-lg" />
                      <span className="hidden sm:inline">{ratio.name}</span>
                    </motion.button>
                  ))}
                </div>
              </div>
            </div>

            {/* MiniMax Resolution Selector */}
            {model === 'minimax' && (
              <div className="mb-8">
                <label className="block text-cyan-400 text-sm font-medium mb-4 flex items-center gap-2">
                  <SafeIcon icon={FiSettings} className="text-lg" />
                  Resolution (MiniMax Only)
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {['512p', '768p', '1080p'].map((res) => (
                    <motion.button
                      key={res}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setResolution(res)}
                      className={`py-3 px-4 rounded-lg border-2 transition-all duration-300 ${
                        resolution === res
                          ? 'border-cyan-400 bg-cyan-400/10 text-cyan-400 shadow-[0_0_15px_rgba(0,255,255,0.3)]'
                          : 'border-gray-600 bg-gray-700/50 text-gray-300 hover:border-gray-500'
                      }`}
                    >
                      {res}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            {errorMessage && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-6 p-4 bg-red-500/10 border border-red-400/30 rounded-xl"
              >
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiAlertCircle} className="text-red-400 text-xl mt-0.5 flex-shrink-0" />
                  <p className="text-red-300 text-sm">{errorMessage}</p>
                </div>
              </motion.div>
            )}

            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleGenerate}
              disabled={(!prompt.trim() && !uploadedImage && !remixVideoId) || isGenerating}
              className={`w-full py-4 rounded-xl font-medium text-lg transition-all duration-300 flex items-center justify-center gap-3 ${
                (!prompt.trim() && !uploadedImage && !remixVideoId) || isGenerating
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-400 hover:to-blue-500 shadow-[0_0_30px_rgba(0,255,255,0.3)] hover:shadow-[0_0_40px_rgba(0,255,255,0.5)]'
              }`}
            >
              {isGenerating ? (
                <>
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  >
                    <SafeIcon icon={FiZap} className="text-xl" />
                  </motion.div>
                  {showRemixInput ? 'Remixing Video...' : 'Generating Video...'}
                </>
              ) : (
                <>
                  <SafeIcon icon={FiPlay} className="text-xl" />
                  {showRemixInput ? 'Remix Video' : 'Generate Video'}
                </>
              )}
            </motion.button>
          </motion.div>

          {/* GENERATED VIDEOS LIST */}
          <AnimatePresence>
            {generatedVideos.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="mt-8"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <h2 className="text-2xl font-semibold text-cyan-400">Generated Videos</h2>
                    <div className="flex items-center gap-2 text-sm text-gray-400">
                      <SafeIcon icon={FiArchive} className="text-base" />
                      <span>Last {MAX_STORED_VIDEOS} generations</span>
                    </div>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleClearAll}
                    className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-400/30 rounded-lg hover:bg-red-500/30 transition-colors duration-300 flex items-center gap-2"
                  >
                    <SafeIcon icon={FiTrash2} />
                    Clear All
                  </motion.button>
                </div>

                <div className="space-y-4">
                  {generatedVideos.map((video) => (
                    <motion.div
                      key={video.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 20 }}
                      className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-6 border border-cyan-400/20 shadow-[0_0_30px_rgba(0,255,255,0.1)]"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1">
                          <div className="aspect-video bg-gray-800 rounded-lg overflow-hidden relative">
                            {video.status === 'completed' && video.url ? (
                              <video src={video.url} controls className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full flex flex-col items-center justify-center p-4">
                                {(video.status === 'processing' || video.status === 'pending' || video.status === 'submitted') && (
                                  <>
                                    <motion.div
                                      animate={{ rotate: 360 }}
                                      transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                    >
                                      <SafeIcon icon={FiZap} className="text-4xl text-cyan-400" />
                                    </motion.div>
                                    <p className="text-gray-400 text-sm mt-4 capitalize">{video.status}...</p>
                                    {video.progress !== undefined && (
                                      <div className="w-full mt-4">
                                        <div className="flex items-center justify-between mb-2">
                                          <span className="text-cyan-400 text-xs font-medium">
                                            {video.progress}%
                                          </span>
                                        </div>
                                        <div className="w-full bg-gray-700 rounded-full h-2">
                                          <motion.div
                                            initial={{ width: 0 }}
                                            animate={{ width: `${video.progress}%` }}
                                            transition={{ duration: 0.5 }}
                                            className="bg-gradient-to-r from-cyan-400 to-blue-500 h-2 rounded-full"
                                          />
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}

                                {(video.status === 'failed' || video.status === 'error' || video.status === 'timeout' || video.status === 'cancelled') && (
                                  <>
                                    <SafeIcon icon={FiAlertCircle} className="text-4xl text-red-400" />
                                    <p className="text-red-400 text-sm mt-4 font-medium uppercase">{video.status}</p>
                                    {video.error && (
                                      <p className="text-red-300 text-xs mt-2 text-center">
                                        {video.error.length > 50 ? video.error.substring(0, 50) + '...' : video.error}
                                      </p>
                                    )}
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="md:col-span-2 flex flex-col justify-between">
                          <div>
                            <div className="flex justify-between items-start">
                              <p className="text-white font-medium mb-2 line-clamp-2">{video.prompt}</p>
                              <button
                                onClick={() => toggleDebug(video.id)}
                                className="text-gray-500 hover:text-cyan-400 transition-colors p-1"
                              >
                                <SafeIcon icon={FiCode} />
                              </button>
                            </div>

                            <div className="flex flex-wrap gap-2 mb-3">
                              <span className="px-3 py-1 bg-cyan-400/10 text-cyan-400 rounded-full text-xs">
                                {video.model}
                              </span>
                              <span className="px-3 py-1 bg-purple-400/10 text-purple-400 rounded-full text-xs">
                                {video.duration}s
                              </span>
                              <span className="px-3 py-1 bg-blue-400/10 text-blue-400 rounded-full text-xs">
                                {video.aspectRatio}
                              </span>
                              {video.hasImage && (
                                <span className="px-3 py-1 bg-green-400/10 text-green-400 rounded-full text-xs flex items-center gap-1">
                                  <SafeIcon icon={FiImage} className="text-[10px]" />
                                  Image
                                </span>
                              )}
                              {video.generationMode === 'video-to-video' && (
                                <span className="px-3 py-1 bg-pink-400/10 text-pink-400 rounded-full text-xs flex items-center gap-1">
                                  <SafeIcon icon={FiRefreshCw} className="text-[10px]" />
                                  Remix
                                </span>
                              )}
                            </div>

                            {expandedDebug === video.id && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                className="mb-3 p-3 bg-black/50 rounded-lg text-xs font-mono text-green-400 overflow-x-auto"
                              >
                                <p className="text-gray-400 mb-1">Task ID: {video.taskId}</p>
                                <p className="text-gray-400 mb-1">Status: {video.status}</p>
                                {video.debugInfo && <pre>{JSON.stringify(video.debugInfo, null, 2)}</pre>}
                              </motion.div>
                            )}

                            <p className="text-gray-400 text-xs">
                              Created: {new Date(video.createdAt).toLocaleString()}
                            </p>
                          </div>

                          <div className="flex flex-wrap gap-3 mt-4">
                            {video.status === 'completed' && (
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleDownload(video)}
                                className="flex-1 px-4 py-2 rounded-lg bg-cyan-500 text-white hover:bg-cyan-400 transition-colors duration-300 flex items-center justify-center gap-2"
                              >
                                <SafeIcon icon={FiDownload} />
                                Download
                              </motion.button>
                            )}

                            {/* REMIX BUTTON FOR SORA-2 */}
                            {video.status === 'completed' && video.model.includes('sora') && (
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleUseForRemix(video)}
                                className="flex-1 px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 border border-purple-400/30 hover:bg-purple-500/30 transition-colors duration-300 flex items-center justify-center gap-2"
                              >
                                <SafeIcon icon={FiRefreshCw} />
                                Remix
                              </motion.button>
                            )}

                            {(video.status === 'timeout' || video.status === 'error' || video.status === 'failed') && (
                              <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={() => handleRetry(video)}
                                className="flex-1 px-4 py-2 rounded-lg bg-yellow-500/20 text-yellow-400 border border-yellow-400/30 hover:bg-yellow-500/30 transition-colors duration-300 flex items-center justify-center gap-2"
                              >
                                <SafeIcon icon={FiRefreshCw} />
                                Retry
                              </motion.button>
                            )}

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handleDelete(video.id)}
                              className="px-4 py-2 bg-red-500/20 text-red-400 border border-red-400/30 rounded-lg hover:bg-red-500/30 transition-colors duration-300 flex items-center justify-center gap-2"
                            >
                              <SafeIcon icon={FiTrash2} />
                              Delete
                            </motion.button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-12 mb-8 space-y-4">
            <motion.a
              href="https://app.base44.com/register?ref=S016XK7V824CI7V1"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full block"
            >
              <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-cyan-600 p-6 rounded-2xl border border-purple-400/30 shadow-[0_0_40px_rgba(168,85,247,0.3)] hover:shadow-[0_0_60px_rgba(168,85,247,0.5)] transition-all duration-300">
                <div className="flex items-center justify-center gap-3">
                  <SafeIcon icon={FiRocket} className="text-3xl text-white" />
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-1">
                      🚀 Built with Base44 - Create Your Own AI App Today!
                    </h3>
                    <p className="text-sm text-gray-200">
                      Click here to start building your own AI-powered application
                    </p>
                  </div>
                </div>
              </div>
            </motion.a>

            <motion.a
              href="https://greta.questera.ai/login?referralCode=i9xku45n"
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="w-full block"
            >
              <div className="bg-gradient-to-r from-cyan-600 via-blue-600 to-indigo-600 p-6 rounded-2xl border border-cyan-400/30 shadow-[0_0_40px_rgba(6,182,212,0.3)] hover:shadow-[0_0_60px_rgba(6,182,212,0.5)] transition-all duration-300">
                <div className="flex items-center justify-center gap-3">
                  <SafeIcon icon={FiCpu} className="text-3xl text-white" />
                  <div className="text-center">
                    <h3 className="text-xl font-bold text-white mb-1">
                      ✨ Greta by Questera - AI Site Builder
                    </h3>
                    <p className="text-sm text-gray-200">
                      Build stunning websites with AI-powered tools
                    </p>
                  </div>
                </div>
              </div>
            </motion.a>
          </div>

          <ApiKeyModal
            isOpen={showApiModal}
            onClose={() => setShowApiModal(false)}
            onSave={handleSaveApiKey}
            currentKey={localStorage.getItem('apimart_api_key') || ''}
          />
        </div>
      </div>

      <footer className="relative z-10 mt-16 pb-8">
        <div className="w-full max-w-6xl mx-auto px-6">
          <div className="bg-gray-800/50 backdrop-blur-xl rounded-2xl p-8 border border-cyan-400/20">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="text-center md:text-left">
                <h3 className="text-cyan-400 font-semibold mb-4 flex items-center justify-center md:justify-start gap-2">
                  <SafeIcon icon={FiShoppingBag} />
                  Shop
                </h3>
                <a
                  href="https://shop.dadda.cool/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg hover:from-cyan-400 hover:to-blue-500 transition-all duration-300 shadow-[0_0_20px_rgba(0,255,255,0.3)] hover:shadow-[0_0_30px_rgba(0,255,255,0.5)] text-sm font-medium"
                >
                  <SafeIcon icon={FiShoppingBag} className="text-base" />
                  Merch
                </a>
              </div>

              <div className="text-center md:text-left">
                <h3 className="text-cyan-400 font-semibold mb-4 flex items-center justify-center md:justify-start gap-2">
                  <SafeIcon icon={FiHeart} />
                  Support
                </h3>
                <a
                  href="https://buymeacoffee.com/daddacool"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg hover:from-pink-400 hover:to-purple-500 transition-all duration-300 shadow-[0_0_20px_rgba(236,72,153,0.3)] hover:shadow-[0_0_30px_rgba(236,72,153,0.5)] text-sm font-medium"
                >
                  <SafeIcon icon={FiHeart} className="text-base" />
                  Support Us
                </a>
              </div>

              <div className="text-center md:text-left">
                <h3 className="text-cyan-400 font-semibold mb-4 flex items-center justify-center md:justify-start gap-2">
                  <SafeIcon icon={FiMail} />
                  Contact
                </h3>
                <a
                  href="https://fiverring.com/#/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-700 text-cyan-400 border border-cyan-400/30 rounded-lg hover:bg-gray-600 transition-all duration-300 text-sm font-medium"
                >
                  <SafeIcon icon={FiMail} className="text-base" />
                  Contact Us
                </a>
              </div>

              <div className="text-center md:text-left">
                <h3 className="text-cyan-400 font-semibold mb-4 flex items-center justify-center md:justify-start gap-2">
                  <SafeIcon icon={FiFileText} />
                  Legal
                </h3>
                <div className="flex flex-col gap-2">
                  <a
                    href="/terms"
                    className="text-gray-300 hover:text-cyan-400 transition-colors text-sm flex items-center justify-center md:justify-start gap-2"
                  >
                    <SafeIcon icon={FiFileText} className="text-xs" />
                    Terms of Service
                  </a>
                  <a
                    href="/privacy"
                    className="text-gray-300 hover:text-cyan-400 transition-colors text-sm flex items-center justify-center md:justify-start gap-2"
                  >
                    <SafeIcon icon={FiShield} className="text-xs" />
                    Privacy Policy
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-6">
              <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                <p className="text-gray-400 text-sm text-center md:text-left">
                  © 2025 <span className="text-cyan-400 font-semibold">@Ai8848</span> · All rights reserved.
                </p>
                <p className="text-gray-500 text-xs text-center md:text-right">
                  Email: <a href="mailto:fiverrings@gmail.com" className="text-cyan-400 hover:text-cyan-300 transition-colors">fiverrings@gmail.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default VideoGenerator;